'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { Avatar, AvatarFallback, AvatarImage, HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger, } from '@artstorefronts/ui';
import { useProfileHoverCard } from './ProfileHoverCardProvider.js';
import './ProfileHoverCard.css';
/** How long the pointer or focus must rest on a trigger before the card opens. Restarts on leave. */
export const OPEN_DELAY_MS = 600;
/** How long after the pointer leaves the trigger, card or actions before the card closes. */
export const CLOSE_DELAY_MS = 150;
function stop(event) {
    event.stopPropagation();
}
// Portaled content still bubbles through the React tree, so a click in the card would reach
// a clickable ancestor (feed card, notification row) without this. Only click: React's
// stopPropagation also stops the native event, and Radix needs pointerdown and pointerup to
// reach the document (outside-dismiss and its pointer-down-on-content latch).
const STOP_PROPAGATION = { onClick: stop };
function initials(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
}
/** displayName → firstName + lastName → slug, the fallback every other profile surface uses. */
export function profileCardName(card) {
    const { user } = card;
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return user.displayName || fullName || user.slug || card.slug;
}
function location(card) {
    return [card.user.city, card.user.region].filter(Boolean).join(', ');
}
function VerifiedShield() {
    return (_jsxs("svg", { className: "phc-tick", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", "aria-hidden": "true", children: [_jsx("path", { d: "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" }), _jsx("path", { d: "m9 12 2 2 4-4" })] }));
}
function PinIcon() {
    return (_jsxs("svg", { className: "phc-pin", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", "aria-hidden": "true", children: [_jsx("path", { d: "M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" }), _jsx("circle", { cx: "12", cy: "10", r: "3" })] }));
}
function Skeleton({ label }) {
    return (_jsxs("div", { className: "phc-skeleton", role: "status", "aria-busy": "true", "aria-label": label, children: [_jsxs("div", { className: "phc-head", children: [_jsx("div", { className: "phc-sk phc-sk-avatar" }), _jsxs("div", { className: "phc-sk-id", children: [_jsx("div", { className: "phc-sk phc-sk-name" }), _jsx("div", { className: "phc-sk phc-sk-loc" })] })] }), _jsxs("div", { className: "phc-body", children: [_jsx("div", { className: "phc-sk phc-sk-line" }), _jsx("div", { className: "phc-sk phc-sk-line phc-sk-w70" }), _jsx("div", { className: "phc-sk phc-sk-line phc-sk-w50" }), _jsxs("div", { className: "phc-grid", children: [_jsx("div", { className: "phc-sk phc-tile" }), _jsx("div", { className: "phc-sk phc-tile" }), _jsx("div", { className: "phc-sk phc-tile" })] }), _jsx("div", { className: "phc-sk phc-sk-cta" })] })] }));
}
function Stats({ card, adapter }) {
    const { strings } = adapter;
    return (_jsxs("div", { className: "phc-stats", children: [_jsx("span", { children: strings.followers(card.user.followerCount ?? 0) }), adapter.showConnectionCount && (_jsxs(_Fragment, { children: [_jsx("span", { className: "phc-dot", "aria-hidden": "true", children: "\u00B7" }), _jsx("span", { children: strings.connections(card.connectionCount) })] }))] }));
}
function Thumbs({ thumbs }) {
    if (!thumbs.length) {
        return null;
    }
    return (_jsx("div", { className: "phc-grid", children: thumbs.map((src, index) => (
        // Two artworks can share a thumbnail URL; position keeps both tiles in this fixed slice.
        _jsx("img", { className: "phc-tile", src: src, alt: "", loading: "lazy" }, `${index}-${src}`))) }));
}
function Body({ card, adapter, actions, }) {
    const { renderProfileLink, strings } = adapter;
    const { user, slug } = card;
    const name = profileCardName(card);
    const place = location(card);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "phc-head", children: [renderProfileLink({
                        slug,
                        className: 'phc-avatar-link',
                        ariaLabel: strings.viewProfile(name),
                        children: (_jsxs(Avatar, { className: "phc-avatar", "data-verified": user.isHumanVerified ? 'true' : 'false', children: [user.avatarUrl && _jsx(AvatarImage, { className: "phc-avatar-image", src: user.avatarUrl, alt: name }), _jsx(AvatarFallback, { className: "phc-avatar-fallback", children: initials(name) })] })),
                    }), _jsxs("div", { className: "phc-id", children: [_jsxs("div", { className: "phc-name", children: [renderProfileLink({ slug, className: 'phc-name-link', children: _jsx("span", { children: name }) }), user.isHumanVerified && _jsx(VerifiedShield, {})] }), place && (_jsxs("div", { className: "phc-loc", children: [_jsx(PinIcon, {}), _jsx("span", { children: place })] }))] })] }), _jsxs("div", { className: "phc-body", children: [user.bio && _jsx("p", { className: "phc-bio", children: user.bio }), _jsx(Stats, { card: card, adapter: adapter }), _jsx(Thumbs, { thumbs: (user.artworkThumbs ?? []).slice(0, 3) }), actions] })] }));
}
/**
 * Wraps one existing profile link or button. Renders it unwrapped unless a provider is mounted
 * and active (adapter enabled, fine hovering pointer). The trigger remounts once when the gate
 * first flips; the price of touch and flag-off markup staying exactly the bare child.
 */
export function ProfileHoverCard({ slug, surface, children }) {
    const context = useProfileHoverCard();
    if (!context?.active) {
        return children;
    }
    return (_jsx(Card, { adapter: context.adapter, slug: slug, surface: surface, children: children }));
}
function Card({ adapter, slug, surface, children }) {
    const [open, setOpen] = useState(false);
    const [hasOpened, setHasOpened] = useState(false);
    const [nullSlug, setNullSlug] = useState(null);
    // True while an open's refetch of a failed card is in flight. The stale error stays on the
    // query meanwhile, so the retry's own promise decides whether the card closes.
    const [retrying, setRetrying] = useState(false);
    // Keyed by the slug it was fetched for, so a trigger reused for another member never shows
    // the previous member's card while the new one loads.
    const [last, setLast] = useState(null);
    // Detached actions host, created on first open: re-parented into each open card, then alive
    // for the trigger's lifetime.
    const [host, setHost] = useState(null);
    const contentRef = useRef(null);
    const overActionsRef = useRef(false);
    const leaveTimerRef = useRef(undefined);
    const reportedRef = useRef(false);
    // Bumped on every open, so a retry settling late never closes a newer open.
    const openGenRef = useRef(0);
    const { data, loading, error, refetch } = adapter.useProfileCard(slug, hasOpened);
    const latched = nullSlug === slug;
    // Render-phase adjustments: close before a null or failed card ever paints.
    if (data && data !== last?.card) {
        setLast({ slug, card: data });
    }
    if (hasOpened && data === null && !loading && !latched) {
        setNullSlug(slug);
        setOpen(false);
    }
    if (open && error && !loading && !retrying) {
        setOpen(false);
    }
    const clearLeave = useCallback(() => {
        clearTimeout(leaveTimerRef.current);
    }, []);
    const close = useCallback(() => {
        clearTimeout(leaveTimerRef.current);
        overActionsRef.current = false;
        setOpen(false);
    }, []);
    useEffect(() => clearLeave, [clearLeave]);
    useEffect(() => {
        if (!open) {
            reportedRef.current = false;
            return;
        }
        if (!data || reportedRef.current) {
            return;
        }
        reportedRef.current = true;
        adapter.onOpened?.(data, surface);
    }, [open, data, adapter, surface]);
    // Stable: a re-render never detaches the host, so a focused action keeps focus.
    const slotRef = useCallback((slot) => {
        if (!slot || !host || host.parentNode === slot) {
            return;
        }
        slot.appendChild(host);
    }, [host]);
    function show() {
        if (open || latched) {
            return;
        }
        clearLeave();
        overActionsRef.current = false;
        openGenRef.current += 1;
        setRetrying(false);
        if (error) {
            retry(openGenRef.current);
        }
        if (!host) {
            setHost(document.createElement('div'));
        }
        setHasOpened(true);
        setOpen(true);
    }
    function retry(gen) {
        setRetrying(true);
        refetch().then(() => {
            settleRetry(gen, false);
        }, () => {
            settleRetry(gen, true);
        });
    }
    function settleRetry(gen, failed) {
        if (gen !== openGenRef.current) {
            return;
        }
        setRetrying(false);
        if (failed) {
            setOpen(false);
        }
    }
    function onOpenChange(next) {
        if (next) {
            show();
            return;
        }
        // Radix's hover-out close fires when the pointer moves from the card onto an action,
        // because the actions sit outside the card in the React tree.
        if (overActionsRef.current) {
            return;
        }
        close();
    }
    function guardOutside(event) {
        if (host?.contains(event.target)) {
            event.preventDefault();
            return;
        }
        overActionsRef.current = false;
    }
    function resetOverActions() {
        overActionsRef.current = false;
    }
    function enterActions() {
        clearLeave();
        overActionsRef.current = true;
    }
    function leaveActions(event) {
        overActionsRef.current = false;
        const to = event.relatedTarget;
        if (to instanceof Node && contentRef.current?.contains(to)) {
            return;
        }
        clearLeave();
        leaveTimerRef.current = setTimeout(close, CLOSE_DELAY_MS);
    }
    const card = data ?? (last?.slug === slug ? last.card : null);
    return (_jsxs(HoverCard, { open: open, onOpenChange: onOpenChange, openDelay: OPEN_DELAY_MS, closeDelay: CLOSE_DELAY_MS, children: [_jsx(HoverCardTrigger, { asChild: true, onPointerEnter: clearLeave, children: children }), _jsx(HoverCardPortal, { children: _jsx(HoverCardContent, { ref: contentRef, className: "phc-content", side: "bottom", align: "start", sideOffset: 8, collisionPadding: 12, onPointerEnter: clearLeave, onPointerDownOutside: guardOutside, onFocusOutside: guardOutside, onEscapeKeyDown: resetOverActions, ...STOP_PROPAGATION, children: data ? (_jsx(Body, { card: data, adapter: adapter, actions: _jsx("div", { className: "phc-actions", ref: slotRef }) })) : (_jsx(Skeleton, { label: adapter.strings.loading })) }) }), host &&
                card &&
                createPortal(_jsx("div", { className: "phc-actions-root", onPointerEnter: enterActions, onPointerLeave: leaveActions, ...STOP_PROPAGATION, children: adapter.renderActions(card, { close }) }), host)] }));
}
//# sourceMappingURL=ProfileHoverCard.js.map